import type { SignagePlaybackReport } from "@shared/types/foyer";

/**
 * The signage surface's view of itself, for the heartbeat to collect.
 *
 * A module-level slot rather than context or an event, because the two parties
 * are not in a parent/child relationship worth wiring: `SignageSurface` knows
 * what is on screen, the heartbeat effect in `App` knows how to talk to the
 * server, and they are siblings that render on completely different cadences
 * (1Hz vs 30s). A shared slot lets the reporter write whenever it likes and the
 * sender read whatever was true at send time - which is exactly the semantics
 * of telemetry.
 *
 * Only the PANEL writes here. The CMS preview renders the same component in
 * someone's browser; it has no heartbeat and nothing to report.
 */

let current: SignagePlaybackReport | null = null;

/**
 * The media element's own account of a stall (`SignagePlaybackReport.media`),
 * written by the Shaka player's progress watchdog and merged in at READ time.
 *
 * Separate from `current` on purpose: `reportSignagePlayback` overwrites
 * `current` whenever the engine's state machine changes, and a stall must
 * survive that. Without `media` the `signage_stalled` alert (which needs
 * `state: "playing"` AND `media`) cannot fire: 2026-09-28 22:00Z a Samsung
 * sat frozen for 8h45m reporting a healthy `playing`.
 */
let mediaStall: string | null = null;

/** Set by the player when it has confirmed a stall; `null` when it recovered
 *  or the player went away. */
export function reportSignageMediaStall(summary: string | null): void {
  mediaStall = summary;
}

export function reportSignagePlayback(report: SignagePlaybackReport): void {
  current = report;
}

/**
 * What to attach to the next heartbeat, or null if this screen is not a
 * signage screen (or has not rendered yet).
 *
 * NOT cleared on `SignageSurface` unmount by itself - the module has no way
 * to know a screen is genuinely done with signage versus, say, a transient
 * remount. `App.tsx` clears it explicitly (`resetSignagePlayback()`) from an
 * effect keyed on the resolved screen role, the moment that role stops being
 * `"signage"`. That used to not exist: before it did, a screen that had ever
 * played signage once kept forwarding its last cached report on every
 * heartbeat forever, because the payload is only absent when this slot was
 * never written - so `ScreenSnapshot.signage` could lie "playing" long after
 * a role flip to `tv`/`door-sign` had already landed correctly. Found and
 * fixed investigating the 2026-08-07 hasselbacken incident - see
 * `tv-realtime-sync` in project memory.
 */
export function currentSignagePlayback(): SignagePlaybackReport | null {
  // Only a screen that claims to be playing can be "stalled": `idle` and
  // `blocked` already say something is wrong or absent, and a leftover stall
  // string must never turn an honest `idle` into a fault.
  if (current && mediaStall && current.state === "playing") {
    return { ...current, media: mediaStall };
  }
  return current;
}

/** Cleared explicitly by `App.tsx` on every role change away from
 * `"signage"`; also a test seam. */
export function resetSignagePlayback(): void {
  current = null;
  mediaStall = null;
}
